import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, ApiRequestError } from "../lib/api";
import { Button, Card, Field, TextField } from "../components/ui";
import type { AuthUser } from "@shared/types";

type Mode = "login" | "register";

const MESSAGES: Record<string, string> = {
  invalid_credentials: "That email or password is incorrect.",
  email_taken: "An account with that email already exists.",
  invalid: "Please check the details you entered.",
  auth_not_configured: "Sign-in is not available right now. Contact support.",
};

function friendlyError(error: unknown): string {
  if (error instanceof ApiRequestError) {
    return MESSAGES[error.body.error] ?? error.body.error;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

export default function Login({ onSuccess }: { onSuccess: () => void }) {
  const [mode, setMode] = useState<Mode>("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });

  const submit = useMutation({
    mutationFn: () => {
      if (mode === "login") {
        return api.post<{ user: AuthUser }>("/auth/login", {
          email: form.email,
          password: form.password,
        });
      }
      return api.post<{ user: AuthUser }>("/auth/register", {
        email: form.email,
        password: form.password,
        name: form.name || undefined,
      });
    },
    onSuccess,
  });

  const switchMode = (next: Mode) => {
    setMode(next);
    submit.reset();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-50 to-white p-4">
      <Card className="w-full max-w-md p-8">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500 font-bold text-white">
            GL
          </span>
          <div>
            <h1 className="text-xl font-semibold">
              {mode === "login" ? "Sign in" : "Create your account"}
            </h1>
            <p className="text-sm text-gray-500">
              {mode === "login"
                ? "Welcome back to General Ledger."
                : "Get started with General Ledger."}
            </p>
          </div>
        </div>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit.mutate();
          }}
        >
          {mode === "register" && (
            <Field label="Name (optional)">
              <TextField
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe"
                autoComplete="name"
              />
            </Field>
          )}
          <Field label="Email">
            <TextField
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@company.com"
              autoComplete="email"
            />
          </Field>
          <Field
            label="Password"
            hint={mode === "register" ? "At least 8 characters." : undefined}
          >
            <TextField
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete={
                mode === "login" ? "current-password" : "new-password"
              }
            />
          </Field>

          {submit.error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {friendlyError(submit.error)}
            </div>
          )}

          <Button type="submit" disabled={submit.isPending} className="w-full">
            {submit.isPending
              ? "Please wait…"
              : mode === "login"
                ? "Sign in"
                : "Create account"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          {mode === "login" ? (
            <>
              Don&apos;t have an account?{" "}
              <button
                className="font-medium text-brand-600 hover:underline"
                onClick={() => switchMode("register")}
              >
                Register
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button
                className="font-medium text-brand-600 hover:underline"
                onClick={() => switchMode("login")}
              >
                Sign in
              </button>
            </>
          )}
        </p>
      </Card>
    </div>
  );
}
